import { type TeamMode } from "@common/constants";
import type { ModeName } from "@common/definitions/modes";

/** the regions as built (the installer writes its own region here) */
const BuiltConfig = {
    regions: {
        main: {
            name: "Trenches",
            mainAddress: "https://trenchio.gg",
            gameAddress: "wss://trenchio.gg/game/<gameID>",
            offset: 1,
            sameOrigin: true
        }
    },
    defaultRegion: "main"
} satisfies ConfigType as ConfigType;

/**
 * A region served by the same front door as the page (the installer's one-server setup, `sameOrigin`): its addresses
 * follow the address the page was opened at, so the site works the same through every way in (the server's own
 * address, its domain, or an entrance such as the China entrance, a relay in Hong Kong) and each connection goes back
 * the way the page came. The built addresses stay as they are anywhere else.
 */
export const fromThisSite = (region: Region, page: Pick<Location, "protocol" | "host" | "origin"> | undefined): Region => {
    if (!region.sameOrigin || !page?.host || !/^https?:$/u.test(page.protocol)) return region;
    const ws = page.protocol === "https:" ? "wss:" : "ws:";
    return {
        ...region,
        mainAddress: page.origin,
        gameAddress: `${ws}//${page.host}/game/<gameID>`
    };
};

export const Config: ConfigType = {
    ...BuiltConfig,
    regions: Object.fromEntries(
        Object.entries(BuiltConfig.regions).map(([id, region]) => [
            id,
            fromThisSite(region, typeof location === "undefined" ? undefined : location)
        ])
    )
};

export interface ConfigType {
    readonly regions: Record<string, Region>
    readonly defaultRegion: string
}

export interface Region {
    /**
     * The human-readable name of the region, displayed in the server selector.
     */
    readonly name: string

    /**
     * An emoji flag to display alongside the region name.
     */
    readonly flag?: string

    /**
     * The address of the region's main server.
     */
    readonly mainAddress: string

    /**
     * Pattern used to determine the address of the region's game servers.
     * The string `<gameID>` is replaced by the `gameID` given by the /getGame API, plus {@linkcode offset}.
     * For example, if `gameID` is 0, `gameAddress` is `"wss://play.example.com/game/<gameID>"`, and `offset` is 1, the resulting address will be wss://play.example.com/game/1.
     */
    readonly gameAddress: string

    /**
     * Number to increment `gameID` by when determining the game address. See {@linkcode gameAddress} for more info.
     */
    readonly offset: number

    /**
     * The region is the site the page itself came from (one front door for the pages, the API and the games): its
     * addresses are taken from the page's own address at load, whichever way the player came in. See {@linkcode fromThisSite}.
     */
    readonly sameOrigin?: boolean
}

export interface ServerInfo {
    readonly protocolVersion: number
    readonly playerCount: number
    readonly teamMode: TeamMode
    readonly teamModeSwitchTime: number
    readonly mode: ModeName
    readonly modeSwitchTime: number
}
